// src/pages/NomReciboPrintPage.jsx
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { obtenerRecibo } from "../services/nominaService";

const money = (n) => Number(n ?? 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt = (d) => {
  if (!d) return "";
  return new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "long", year: "numeric" });
};
const TIPO_PER = { S: "Semanal", C: "Catorcenal", Q: "Quincenal", M: "Mensual" };

export default function NomReciboPrintPage() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const printRef = useRef();

  useEffect(() => {
    obtenerRecibo(id)
      .then(setData)
      .catch(() => setError("Error al cargar el recibo"));
  }, [id]);

  if (error) return <div style={{ padding: 32, color: "red" }}>{error}</div>;
  if (!data) return <div style={{ padding: 32 }}>Cargando recibo...</div>;

  const handlePrint = () => window.print();

  const totalDeduccionesCompleto = (data.totalDeducciones ?? 0) + (data.imssObrero ?? 0) + (data.isr ?? 0) - (data.subsidioEmpleo ?? 0);

  return (
    <>
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; }
        }
        @media screen {
          body { background: #e5e7eb; }
        }
        .recibo { background: white; width: 21cm; margin: 0 auto; padding: 1.5cm; font-family: Arial, sans-serif; font-size: 11pt; }
        .recibo table { width: 100%; border-collapse: collapse; }
        .recibo th { background: #1e3a5f; color: white; padding: 6px 8px; text-align: left; font-size: 10pt; }
        .recibo td { padding: 4px 8px; border-bottom: 1px solid #e5e7eb; font-size: 10pt; }
        .right { text-align: right; }
        .total-row td { font-weight: bold; background: #f8fafc; }
      `}</style>

      <div className="no-print" style={{ padding: 16, background: "#f1f5f9", display: "flex", gap: 8, alignItems: "center" }}>
        <button onClick={handlePrint} style={{ padding: "8px 20px", background: "#1e3a5f", color: "white", border: "none", borderRadius: 6, cursor: "pointer", fontWeight: 600 }}>
          Imprimir
        </button>
        <button onClick={() => window.history.back()} style={{ padding: "8px 20px", border: "1px solid #cbd5e1", borderRadius: 6, cursor: "pointer", background: "#fff" }}>
          ← Volver
        </button>
      </div>

      <div className="recibo" ref={printRef}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20 }}>
          <img src="/LMD.jpg" alt="Logo" style={{ height: 50, objectFit: "contain" }} onError={e => { e.target.style.display = "none"; }} />
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: "#1e3a5f" }}>{data.empresaNombre}</div>
            <div style={{ fontSize: 10, color: "#64748b" }}>RFC: {data.empresaRfc}</div>
          </div>
        </div>

        <div style={{ textAlign: "center", marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#1e3a5f", textTransform: "uppercase", letterSpacing: 1 }}>
            Recibo de Nómina — {TIPO_PER[data.tipoPeriodo] ?? data.tipoPeriodo}
          </div>
          <div style={{ fontSize: 11, color: "#64748b" }}>
            {data.periodoNombre} &nbsp;|&nbsp; {fmt(data.fechaInicio)} al {fmt(data.fechaFin)}
          </div>
        </div>

        <table style={{ marginBottom: 16, border: "1px solid #e5e7eb" }}>
          <tbody>
            <tr>
              <td style={{ width: "20%", color: "#64748b", fontWeight: 600 }}>Clave:</td>
              <td style={{ width: "30%" }}>{data.clave}</td>
              <td style={{ width: "20%", color: "#64748b", fontWeight: 600 }}>Nombre:</td>
              <td style={{ width: "30%" }}>{data.nombreCompleto}</td>
            </tr>
            <tr>
              <td style={{ color: "#64748b", fontWeight: 600 }}>RFC:</td>
              <td>{data.rfc}</td>
              <td style={{ color: "#64748b", fontWeight: 600 }}>NSS:</td>
              <td>{data.nss ?? "—"}</td>
            </tr>
            <tr>
              <td style={{ color: "#64748b", fontWeight: 600 }}>Departamento:</td>
              <td>{data.departamento ?? "—"}</td>
              <td style={{ color: "#64748b", fontWeight: 600 }}>Puesto:</td>
              <td>{data.puesto ?? "—"}</td>
            </tr>
            <tr>
              <td style={{ color: "#64748b", fontWeight: 600 }}>Sueldo Diario:</td>
              <td>${money(data.sueldoDiario)}</td>
              <td style={{ color: "#64748b", fontWeight: 600 }}>Días Laborados:</td>
              <td>{data.diasLaborados}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
          <div>
            <table>
              <thead><tr><th colSpan={2}>Percepciones</th></tr></thead>
              <tbody>
                {data.percepciones.map((p, i) => (
                  <tr key={i}>
                    <td>{p.descripcion}</td>
                    <td className="right">${money(p.importe)}</td>
                  </tr>
                ))}
                <tr className="total-row">
                  <td>Total Percepciones</td>
                  <td className="right">${money(data.totalPercepciones)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div>
            <table>
              <thead><tr><th colSpan={2}>Deducciones</th></tr></thead>
              <tbody>
                {data.deducciones.map((d, i) => (
                  <tr key={i}>
                    <td>{d.descripcion}</td>
                    <td className="right">${money(d.importe)}</td>
                  </tr>
                ))}
                <tr>
                  <td>IMSS Obrero</td>
                  <td className="right">${money(data.imssObrero)}</td>
                </tr>
                <tr>
                  <td>ISR Retenido</td>
                  <td className="right">${money(data.isr)}</td>
                </tr>
                {data.subsidioEmpleo > 0 && (
                  <tr>
                    <td>Subsidio al Empleo</td>
                    <td className="right" style={{ color: "#16a34a" }}>-${money(data.subsidioEmpleo)}</td>
                  </tr>
                )}
                <tr className="total-row">
                  <td>Total Deducciones</td>
                  <td className="right">${money(totalDeduccionesCompleto)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <div style={{ textAlign: "right", padding: "12px 16px", background: "#1e3a5f", color: "white", borderRadius: 6, fontSize: 16, fontWeight: 700 }}>
          Neto a Pagar: ${money(data.netoPagar)}
        </div>

        <div style={{ marginTop: 48, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 48 }}>
          <div style={{ borderTop: "1px solid #94a3b8", paddingTop: 8, textAlign: "center", color: "#64748b", fontSize: 10 }}>
            Firma del Trabajador
          </div>
          <div style={{ borderTop: "1px solid #94a3b8", paddingTop: 8, textAlign: "center", color: "#64748b", fontSize: 10 }}>
            Firma del Patrón
          </div>
        </div>
      </div>
    </>
  );
}
